import { FC } from "react";

import { Box, Grid } from "@mui/material";
import { Locales, getCurrencyFromCountry } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { isMin, isRequired, isMax } from "@/common/helpers/validators";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardAmountInput } from "@/dashboard-design-system/DashboardAmountInput";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import {
  Amount,
  DefaultFrequencyValue,
  FrequencyAmountsName,
  StepSuggestedDonationAmountsFormData,
} from "@/features/LiveFormEditor/Steps/StepSuggestedDonationAmounts/types";
import { DashboardTranslationKey } from "@/public/@types/translations";

const MIN_READABLE_FIELD_WIDTH = 68;
const FIELD_GAP = 8;

interface AmountsInputsArrayProps {
  inputsArray: Amount[];
  form: UseFormReturn<StepSuggestedDonationAmountsFormData>;
  name: FrequencyAmountsName;
  placeholders: DashboardTranslationKey[];
  locale: Locales;
}

export const AmountsInputsArray: FC<AmountsInputsArrayProps> = ({ form, inputsArray, name, locale }) => {
  const { t } = useTranslate();
  const { apiFormObject, editOrCreateMode, isOnboarding, setPreviewAmountFocus } = useLiveFormEditorContext();
  const formCountry = apiFormObject.organization?.country;
  const { logAmplitudeEvent } = useAmplitude();

  const handleInputChange = (index: number) => (amount: number | null) => {
    form.setValue(`${name}.${index}.amount`, amount, { shouldDirty: true });
  };

  const currency = getCurrencyFromCountry(formCountry);

  const recurrenceInterval = name.replace(/Amounts$/, "") as DefaultFrequencyValue;

  const onAmountInputFocusInternal = (index: number) => () => {
    logAmplitudeEvent(AmplitudeEvents.CustomizationFocusAmount, {
      isEditing: editOrCreateMode === "edit",
      isOnboarding,
    });
    setPreviewAmountFocus({ recurrenceInterval, amountIndex: index });
  };

  const { errors } = form.formState;

  const renderAmountRow = (amount: Amount, index: number) => {
    const inputNamePrefix = `${name}.${index}` as const;
    const errorAmount = errors[name]?.[index]?.amount;

    return (
      <Box key={amount.id} sx={{ flex: 1, minWidth: 0, display: "flex" }}>
        <Grid container sx={{ width: "100%" }}>
          <input type="hidden" {...form.register(`${inputNamePrefix}.id`)} />
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <DashboardAmountInput
              control={form.control}
              locale={locale}
              currency={currency}
              name={`${inputNamePrefix}.amount`}
              onChange={handleInputChange(index)}
              onFocus={onAmountInputFocusInternal(index)}
              rules={[
                isRequired(t("dashboard", "donationForm.amountsInputsRequired")),
                isMin(100, t("dashboard", "donationForm.amountsInputsMinimum")),
                isMax(1000000, t("dashboard", "donationForm.amountsInputsMaximum")),
              ]}
              errorMessage={errorAmount?.message}
              defaultValue={amount.amount ?? undefined}
              data-test="amounts-inputs-array-amount-input"
              shouldUnregister={false}
              debounced={300}
            />
          </Box>
        </Grid>
      </Box>
    );
  };

  const fieldCount = inputsArray.length + 1;
  const singleRowMinWidth = fieldCount * MIN_READABLE_FIELD_WIDTH + (fieldCount - 1) * FIELD_GAP;

  return (
    <Box sx={{ containerType: "inline-size", flex: 1, minWidth: 0 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: `${FIELD_GAP}px`,
          [`@container (min-width: ${singleRowMinWidth}px)`]: {
            display: "flex",
            gridTemplateColumns: "unset",
          },
        }}
      >
        {inputsArray.map((amount, index) => renderAmountRow(amount, index))}
        <DashboardTooltip
          title={t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.otherAmount.tooltip")}
          placement="top"
          slotProps={{ tooltip: { sx: (theme) => ({ ...theme.typography.caption, maxWidth: "none" }) } }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }} data-test="amounts-inputs-array-other-amount">
            <DashboardTextField
              placeholder={t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.otherAmount")}
              disabled
            />
          </Box>
        </DashboardTooltip>
      </Box>
    </Box>
  );
};
